import {
  Audience,
  AudienceFilter,
  AudienceFilterOperator,
  AudienceFilterTimeOperator,
  AudienceFilterTimeValue,
  AudienceFilterType,
} from '../../audience/components/audience.model';
import { DataPlatformAudienceRule, DataPlatformEventFilter, DataPlatformScoreBand } from '../data-platform.model';
import {
  comparableRules,
  frequencyOf,
  isoInstant,
  matchedValues,
  operatorOf,
  parseBool,
  revenueComparison,
  timeframeOf,
} from './comparable-rules.utils';
import { dayRangeProblem, rulesToAudienceFilters } from './rule-to-audience-filter.utils';

/**
 * DP rule types that read live visit state (the customer's site auth), not the
 * stored profile. The DataPlatform count engine silently ignores rule types it
 * doesn't recognise and returns a count over whatever's left, so an audience
 * containing one must show N/A reach rather than a misleading all-profiles number.
 */
export const VISIT_BASED_RULE_TYPES: ReadonlySet<string> = new Set<string>(['is_logged_in', 'is_logged_out']);

/** Any rule the count engine can't evaluate (see VISIT_BASED_RULE_TYPES). */
export function hasUncountableRule(rules: readonly DataPlatformAudienceRule[]): boolean {
  return rules.some((r) => VISIT_BASED_RULE_TYPES.has(r.type));
}

const EVALUATE_RULE_TYPES: ReadonlySet<string> = new Set([
  'event_count',
  'goal_count',
  'widget_event_count',
  'revenue',
  'last_active',
  'first_seen',
  'total_events',
  'session_count',
  'purchase_count',
  'engagement_score',
  'has_tag',
  'has_all_actions',
  'has_any_action',
  'has_consent',
  'has_not_consent',
  'dimensions',
  'dimension_affinity',
  'is_identified',
  'is_anonymous',
]);

const EVALUATE_LIFETIME_RULE_TYPES: ReadonlySet<string> = new Set([
  'event_count',
  'goal_count',
  'widget_event_count',
  'total_events',
  'session_count',
  'purchase_count',
  'revenue',
]);

/** Evaluate has no windows on counts or revenue, value lists, coupons or day ranges, and reads `between` only on revenue. */
export function isEvaluable(rules: readonly DataPlatformAudienceRule[]): boolean {
  return rules.every(
    (rule) =>
      EVALUATE_RULE_TYPES.has(rule.type) &&
      !rule.values?.length &&
      !rule.couponOnly &&
      rule.daysAgoUpper == null &&
      !(EVALUATE_LIFETIME_RULE_TYPES.has(rule.type) && hasTimeframe(rule)) &&
      (rule.operator !== 'between' || rule.type === 'revenue'),
  );
}

/** Evaluate and the daily count read one tag or consent version, not a `values` list. */
export function withSingleValue(rule: DataPlatformAudienceRule): DataPlatformAudienceRule {
  if (rule.values?.length !== 1) {
    return rule;
  }
  const { values, ...rest } = rule;
  if (rule.type === 'has_tag') {
    return { ...rest, tag: values[0] };
  }
  if (rule.type === 'has_consent' || rule.type === 'has_not_consent') {
    return { ...rest, versionDate: values[0] };
  }
  return rule;
}

/** The audience as evaluate rules; null when evaluate can't read it the way the onsite check does. */
export function audienceEvaluateRules(audience: Audience): DataPlatformAudienceRule[] | null {
  if (audience.sessionConditions?.length || !audience.filters?.length) {
    return null;
  }
  const rules: DataPlatformAudienceRule[] = [];
  for (const filter of audience.filters) {
    const mapped = comparableRules(filter);
    if (!mapped) {
      return null;
    }
    rules.push(...mapped.map(withSingleValue));
  }
  return isEvaluable(rules) ? rules : null;
}

export function hasTimeframe(rule: DataPlatformAudienceRule): boolean {
  return rule.daysAgo != null || rule.daysAgoUpper != null || !!rule.startDate || !!rule.endDate;
}

export interface RuleSource {
  filter: AudienceFilter;
  rules: DataPlatformAudienceRule[];
}

/**
 * Full filter → rule mapper. The counterpart to `rule-to-audience-filter.utils.ts`,
 * used by the flyout audience hub to load an existing audience and edit it
 * natively.
 *
 * `passthroughFilters` are saved back unchanged: types without a rule, and filters the rules can't carry exactly.
 */
export function audienceToDpRules(audience: Audience): {
  rules: DataPlatformAudienceRule[];
  passthroughFilters: AudienceFilter[];
  sources: RuleSource[];
  warnings: string[];
} {
  const rules: DataPlatformAudienceRule[] = [];
  const passthroughFilters: AudienceFilter[] = [];
  const sources: RuleSource[] = [];
  const warnings: string[] = [];

  if (audience.sessionConditions?.length) {
    warnings.push(
      `${audience.sessionConditions.length} session condition(s) cannot be mapped — only audience filters are supported.`,
    );
  }

  for (const filter of audience.filters ?? []) {
    if (isPassthroughType(filter.type)) {
      passthroughFilters.push(filter);
      continue;
    }
    if (EXACT_FILTER_TYPES.has(filter.type ?? '')) {
      const mapped = exactEditorRules(filter);
      if (mapped && keepsOnsiteMeaning(filter, mapped)) {
        rules.push(...mapped);
        sources.push({ filter, rules: mapped });
      } else {
        passthroughFilters.push(filter);
        warnings.push(`${filter.type} filter is kept as stored: the rule editor cannot show it exactly.`);
      }
      continue;
    }
    const before = rules.length;
    switch (filter.type) {
      case AudienceFilterType.LatestSession: {
        const daysAgo = parseInt(filter.value as string, 10);
        if (isNaN(daysAgo)) {
          warnings.push('Latest Session filter has no days value — skipped.');
          break;
        }
        const rule: DataPlatformAudienceRule = {
          type: 'last_active',
          operator: filterOpToDpOp(filter.operator),
          daysAgo,
        };
        if (filter.upperValue != null) rule.daysAgoUpper = filter.upperValue;
        rules.push(rule);
        break;
      }

      case AudienceFilterType.FirstSeen: {
        const daysAgo = parseInt(filter.value as string, 10);
        if (isNaN(daysAgo)) {
          warnings.push('First Seen filter has no days value — skipped.');
          break;
        }
        const rule: DataPlatformAudienceRule = {
          type: 'first_seen',
          operator: filterOpToDpOp(filter.operator),
          daysAgo,
        };
        if (filter.upperValue != null) rule.daysAgoUpper = filter.upperValue;
        rules.push(rule);
        break;
      }

      case AudienceFilterType.EventCount: {
        const value = parseInt(filter.value as string, 10);
        if (isNaN(value)) {
          warnings.push('EventCount filter has no value — skipped.');
          break;
        }
        const eventName = filter.eventProperties?.eventName;
        const tf = parseTimeframe(filter);
        const rule: DataPlatformAudienceRule = {
          type: eventName ? 'event_count' : 'total_events',
          operator: filterOpToDpOp(filter.operator),
          value,
          ...tf,
        };
        if (eventName) rule.eventName = eventName;
        if (filter.upperValue != null) rule.upperValue = filter.upperValue;
        rules.push(rule);
        break;
      }

      case AudienceFilterType.WidgetEventCount: {
        const value = parseInt(filter.value as string, 10);
        if (isNaN(value)) {
          warnings.push('WidgetEventCount filter has no value — skipped.');
          break;
        }
        const tf = parseTimeframe(filter);
        const rule: DataPlatformAudienceRule = {
          type: 'widget_event_count',
          operator: filterOpToDpOp(filter.operator),
          value,
          widgetId: filter.widgetEventProperties?.widgetId,
          widgetEventType: filter.widgetEventProperties?.widgetEventType,
          ...tf,
        };
        if (filter.upperValue != null) rule.upperValue = filter.upperValue;
        rules.push(rule);
        break;
      }

      case AudienceFilterType.SessionCount: {
        const value = parseInt(filter.value as string, 10);
        if (isNaN(value)) {
          warnings.push('SessionCount filter has no value — skipped.');
          break;
        }
        const tf = parseTimeframe(filter);
        const rule: DataPlatformAudienceRule = {
          type: 'session_count',
          operator: filterOpToDpOp(filter.operator),
          value,
          ...tf,
        };
        if (filter.upperValue != null) rule.upperValue = filter.upperValue;
        rules.push(rule);
        break;
      }

      case AudienceFilterType.Dimension: {
        const value = parseInt(filter.value as string, 10);
        rules.push({
          type: 'dimensions',
          operator: filterOpToDpOp(filter.operator),
          value: isNaN(value) ? 1 : value,
          dimension: filter.dimensionProperties?.dimension,
        });
        break;
      }

      case AudienceFilterType.DimensionAffinity: {
        const value = parseInt(filter.value as string, 10);
        const rule: DataPlatformAudienceRule = {
          type: 'dimension_affinity',
          operator: filterOpToDpOp(filter.operator),
          value: isNaN(value) ? 50 : value,
          dimension: filter.dimensionProperties?.dimension,
        };
        if (filter.upperValue != null) rule.upperValue = filter.upperValue;
        rules.push(rule);
        break;
      }

      case AudienceFilterType.EngagementScore: {
        const value = parseInt(filter.value as string, 10);
        const rule: DataPlatformAudienceRule = {
          type: 'engagement_score',
          operator: filterOpToDpOp(filter.operator),
          value: isNaN(value) ? 50 : value,
        };
        if (filter.upperValue != null) rule.upperValue = filter.upperValue;
        rules.push(rule);
        break;
      }

      default:
        warnings.push(`Filter type "${filter.type}" cannot be mapped to a data-platform rule.`);
    }
    if (rules.length > before) {
      sources.push({ filter, rules: rules.slice(before) });
    }
  }

  return { rules, passthroughFilters, sources, warnings };
}

const EXACT_FILTER_TYPES: ReadonlySet<string> = new Set([
  AudienceFilterType.Identified,
  AudienceFilterType.LoggedIn,
  AudienceFilterType.Consent,
  AudienceFilterType.Goals,
  AudienceFilterType.Tags,
  AudienceFilterType.Purchase,
]);

function exactEditorRules(filter: AudienceFilter): DataPlatformAudienceRule[] | null {
  switch (filter.type) {
    case AudienceFilterType.Identified:
      return comparableRules(filter);
    case AudienceFilterType.LoggedIn: {
      const loggedIn = operatorOf(filter) === AudienceFilterOperator.Equals ? parseBool(filter.value) : null;
      return loggedIn === null ? null : [{ type: loggedIn ? 'is_logged_in' : 'is_logged_out' }];
    }
    case AudienceFilterType.Consent: {
      if (operatorOf(filter) === AudienceFilterOperator.Exists) {
        return comparableRules(filter);
      }
      const versions = matchedValues(filter);
      if (versions?.values.length !== 1) {
        return null;
      }
      return [
        {
          type: versions.negated ? 'has_not_consent' : 'has_consent',
          versionDate: exactSingleValue(filter, versions.values[0]),
        },
      ];
    }
    case AudienceFilterType.Goals:
      return (
        comparableRules(filter)?.map((rule) =>
          rule.eventName
            ? { ...rule, eventName: exactSingleValue(filter, rule.eventName) }
            : { ...rule, values: unique(rule.values ?? []) },
        ) ?? null
      );
    case AudienceFilterType.Tags: {
      const tags = matchedValues(filter);
      if (!tags) {
        return null;
      }
      const type = tags.negated ? 'has_not_tag' : 'has_tag';
      const values = unique(tags.values);
      return [values.length === 1 ? { type, tag: exactSingleValue(filter, values[0]) } : { type, values }];
    }
    case AudienceFilterType.Purchase:
      return purchaseEditorRules(filter);
    default:
      return null;
  }
}

/** A revenue rule already needs a purchase, so "at least once" is left out. */
function purchaseEditorRules(filter: AudienceFilter): DataPlatformAudienceRule[] | null {
  const timeframe = timeframeOf(filter);
  if (!timeframe) {
    return null;
  }
  const properties = filter.purchaseProperties;
  const operator = operatorOf(filter);
  if (operator === AudienceFilterOperator.NotEquals) {
    if (properties?.appliedCoupon || (properties && !revenueComparison(properties).holdsAtZero)) {
      return null;
    }
    return [{ type: 'purchase_count', operator: 'eq', value: 0, ...timeframe }];
  }
  const frequency = operator === AudienceFilterOperator.Equals ? frequencyOf(filter) : null;
  if (!frequency) {
    return null;
  }
  const count: DataPlatformAudienceRule = { type: 'purchase_count', ...frequency, ...timeframe };
  if (!properties) {
    return [count];
  }
  const range = revenueComparison(properties).range;
  if (!range) {
    return null;
  }
  const revenue: DataPlatformAudienceRule = {
    type: 'revenue',
    ...range,
    ...timeframe,
    ...(properties.appliedCoupon ? { couponOnly: true } : {}),
  };
  const impliedByRevenue = frequency.operator === 'gte' && frequency.value === 1;
  return impliedByRevenue ? [revenue] : [count, revenue];
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values)];
}

/** The check compares a single value untrimmed and trims list items. */
function exactSingleValue(filter: AudienceFilter, trimmed: string): string {
  const operator = operatorOf(filter);
  const raw = String(filter.value ?? '');
  const single = operator === AudienceFilterOperator.Equals || operator === AudienceFilterOperator.NotEquals;
  return single && raw.trim() === trimmed ? raw : trimmed;
}

function keepsOnsiteMeaning(filter: AudienceFilter, mapped: DataPlatformAudienceRule[]): boolean {
  // Login state has no count rule to compare; only its Equals true/false form is mapped.
  if (filter.type === AudienceFilterType.LoggedIn) {
    return true;
  }
  const stored = comparableRules(filter);
  const { filters, unsavable } = rulesToAudienceFilters(mapped);
  if (!stored || unsavable.length > 0 || filters.length === 0) {
    return false;
  }
  const roundTripped: DataPlatformAudienceRule[] = [];
  for (const saved of filters) {
    const rules = comparableRules(saved);
    if (!rules) {
      return false;
    }
    roundTripped.push(...rules);
  }
  return sameRules(stored, roundTripped);
}

/** The check reads a value list as a set. */
function sameRules(a: readonly DataPlatformAudienceRule[], b: readonly DataPlatformAudienceRule[]): boolean {
  const keys = (rules: readonly DataPlatformAudienceRule[]) =>
    rules.map((rule) => ruleKey(rule.values ? { ...rule, values: [...new Set(rule.values)].sort() } : rule)).sort();
  return JSON.stringify(keys(a)) === JSON.stringify(keys(b));
}

/** Untouched loaded filters are saved exactly as stored. */
export function filtersForSave(
  rules: readonly DataPlatformAudienceRule[],
  sources: readonly RuleSource[],
): { filters: AudienceFilter[]; unsavable: DataPlatformAudienceRule[] } {
  const remaining = [...rules];
  const kept: AudienceFilter[] = [];
  for (const source of sources) {
    const matched: number[] = [];
    for (const original of source.rules) {
      const key = ruleKey(original);
      const index = remaining.findIndex((rule, i) => !matched.includes(i) && ruleKey(rule) === key);
      if (index < 0) {
        break;
      }
      matched.push(index);
    }
    if (matched.length === source.rules.length) {
      kept.push(source.filter);
      matched.sort((a, b) => b - a).forEach((index) => remaining.splice(index, 1));
    }
  }
  const invalidRanges = remaining.filter((rule) => dayRangeProblem(rule));
  const converted = rulesToAudienceFilters(remaining.filter((rule) => !invalidRanges.includes(rule)));
  return { filters: [...kept, ...converted.filters], unsavable: [...invalidRanges, ...converted.unsavable] };
}

export function ruleKey(rule: DataPlatformAudienceRule): string {
  const entries = Object.entries(rule)
    .filter(([, value]) => value !== undefined && value !== null)
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}

/**
 * Filter types that have no native DP rule equivalent today — they stay as
 * legacy `AudienceFilter` objects and are surfaced via click-to-edit chips
 * (using the legacy `FilterItemDialogComponent` for the actual editing).
 */
function isPassthroughType(type: AudienceFilterType | undefined): boolean {
  if (!type) return false;
  return (
    type === AudienceFilterType.AppList ||
    type === AudienceFilterType.AppSegment ||
    type === AudienceFilterType.AppLabels ||
    type === AudienceFilterType.AppTags ||
    type === AudienceFilterType.Country ||
    type === AudienceFilterType.Region ||
    type === AudienceFilterType.NpsScore ||
    type === AudienceFilterType.CsatScore ||
    type === AudienceFilterType.Weather ||
    type === AudienceFilterType.Temperature ||
    type === AudienceFilterType.InterestProfiles
  );
}

function filterOpToDpOp(op?: AudienceFilterOperator): string {
  switch (op) {
    case AudienceFilterOperator.Equals:
      return 'eq';
    case AudienceFilterOperator.GreaterThan:
      return 'gt';
    case AudienceFilterOperator.GreaterThanOrEqual:
      return 'gte';
    case AudienceFilterOperator.LessThan:
      return 'lt';
    case AudienceFilterOperator.LessThanOrEqual:
      return 'lte';
    case AudienceFilterOperator.Between:
      return 'between';
    default:
      return 'gte';
  }
}

function parseTimeframe(filter: AudienceFilter): Partial<DataPlatformAudienceRule> {
  const tOp = filter.timeOperator;
  const tValRaw = filter.timeValue;
  if (!tOp || tOp === AudienceFilterTimeOperator.AnyTime || !tValRaw) return {};
  let tVal: AudienceFilterTimeValue;
  try {
    tVal = (typeof tValRaw === 'string' ? JSON.parse(tValRaw) : tValRaw) as AudienceFilterTimeValue;
  } catch {
    return {};
  }
  if (tOp === AudienceFilterTimeOperator.TimeWindow && tVal.days != null) {
    return { daysAgo: tVal.days };
  }
  if (tOp === AudienceFilterTimeOperator.BetweenDaysAgo && tVal.days != null && tVal.upperDays != null) {
    return { daysAgo: tVal.days, daysAgoUpper: tVal.upperDays };
  }
  if (tOp === AudienceFilterTimeOperator.SinceDate && tVal.startDate) {
    const startDate = isoInstant(tVal.startDate);
    return startDate ? { startDate } : {};
  }
  if (tOp === AudienceFilterTimeOperator.BetweenDates && tVal.startDate && tVal.endDate) {
    const startDate = isoInstant(tVal.startDate);
    const endDate = isoInstant(tVal.endDate);
    return startDate && endDate ? { startDate, endDate } : {};
  }
  return {};
}

/**
 * Builds a flat `DataPlatformEventFilter` that scopes a breakdown to (an
 * approximation of) an audience's population — used to make device/country/
 * source/utm pass-rate estimates "among this audience" rather than site-wide.
 *
 * Returns `null` when the audience has any rule the flat filter can't represent
 * (revenue, purchase, last_active, event_count, is_identified, …). Bailing —
 * rather than dropping the restrictive rule — keeps the scoped population from
 * silently becoming broader than the audience; the caller then falls back to the
 * site-wide distribution. An empty rule set returns the site-wide filter as-is.
 *
 * `baseFilter` supplies `siteIds` + `from`/`to`; only those carry over.
 */
export function rulesToBreakdownFilter(
  rules: DataPlatformAudienceRule[],
  baseFilter: DataPlatformEventFilter,
): DataPlatformEventFilter | null {
  const out: DataPlatformEventFilter = { siteIds: baseFilter.siteIds, from: baseFilter.from, to: baseFilter.to };
  const goals: string[] = [];
  const tags: string[] = [];
  const dimensions: string[] = [];
  const bands: DataPlatformScoreBand[] = [];

  for (const rule of rules) {
    switch (rule.type) {
      case 'goal_count':
        if (!rule.eventName || countIncludesZero(rule)) {
          return null;
        }
        goals.push(rule.eventName);
        break;
      case 'has_tag': {
        const tag = rule.tag ?? (rule.values?.length === 1 ? rule.values[0] : undefined);
        if (!tag) {
          return null;
        }
        tags.push(tag);
        break;
      }
      case 'dimensions':
        if (!rule.dimension) return null;
        dimensions.push(rule.dimension);
        break;
      case 'engagement_score': {
        const band = ruleToScoreBand(rule);
        if (!band) return null;
        bands.push(band);
        break;
      }
      case 'has_consent':
      case 'has_not_consent':
        if (rule.versionDate || rule.values?.length) {
          return null;
        }
        out.consentFilter = rule.type === 'has_consent' ? 'has_consent' : 'has_not_consent';
        break;
      case 'session_count':
        if (!applySessionCountToFilter(rule, out)) return null;
        break;
      default:
        // Anything else (revenue, event_count, last_active, is_identified, …)
        // can't be expressed as a flat filter — can't scope safely.
        return null;
    }
  }

  if (goals.length) out.goals = goals;
  if (tags.length) out.tags = tags;
  if (dimensions.length) out.dimensions = dimensions;
  if (bands.length) out.engagementScoreBands = bands;
  return out;
}

/** The breakdown's goal list only matches people with the goal, so a count that includes zero can't use it. */
function countIncludesZero(rule: DataPlatformAudienceRule): boolean {
  const value = rule.value ?? 1;
  switch (rule.operator) {
    case 'lt':
    case 'lte':
      return true;
    case 'eq':
    case 'gte':
      return value <= 0;
    default:
      return false;
  }
}

function ruleToScoreBand(rule: DataPlatformAudienceRule): DataPlatformScoreBand | null {
  if (rule.value == null) return null;
  const v = rule.value;
  const clamp = (n: number) => Math.max(0, Math.min(100, n));
  switch (rule.operator) {
    case 'gte':
      return { min: clamp(v), max: 100 };
    case 'gt':
      return { min: clamp(v + 1), max: 100 };
    case 'lte':
      return { min: 0, max: clamp(v) };
    case 'lt':
      return { min: 0, max: clamp(v - 1) };
    case 'eq':
      return { min: clamp(v), max: clamp(v) };
    // A `between` without an upper bound can't be represented without widening
    // the band → bail (caller falls back to site-wide), per the no-broadening rule.
    case 'between':
      return rule.upperValue == null ? null : { min: clamp(v), max: clamp(rule.upperValue) };
    default:
      return { min: clamp(v), max: 100 };
  }
}

function applySessionCountToFilter(rule: DataPlatformAudienceRule, out: DataPlatformEventFilter): boolean {
  if (rule.value == null) return false;
  const v = rule.value;
  switch (rule.operator) {
    case 'gte':
      out.minVisits = v;
      return true;
    case 'gt':
      out.minVisits = v + 1;
      return true;
    case 'lte':
      out.maxVisits = v;
      return true;
    case 'lt':
      out.maxVisits = v - 1;
      return true;
    case 'eq':
      out.minVisits = v;
      out.maxVisits = v;
      return true;
    // Without an upper bound this would broaden to an open-ended `>= v` → bail.
    case 'between':
      if (rule.upperValue == null) return false;
      out.minVisits = v;
      out.maxVisits = rule.upperValue;
      return true;
    default:
      out.minVisits = v;
      return true;
  }
}
